import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiOpenSourceLine } from '@remixicon/react';
import {
  CopyButton,
  ExternalLink,
  InfraField,
  InfraSection,
  InfraStack,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';
import { LabOcSection } from './labOc';

const secretPreview = (value: string, max = 48) => {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max)}… (${value.length} chars)`;
};

export const OpenShiftInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};
  const templateType = annotations['infrastructure.backstage.io/type'];

  if (templateType !== 'gcp-openshift') {
    return null;
  }

  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const clusterName = annotations['openshift.infrastructure/cluster-name'];
  const apiUrl = annotations['openshift.infrastructure/api-url'];
  const consoleUrl = annotations['openshift.infrastructure/console-url'];
  const appsDomain = annotations['openshift.infrastructure/apps-domain'];
  const version = annotations['openshift.infrastructure/version'];
  const workerType = annotations['openshift.infrastructure/worker-machine-type'];
  const workerCount = annotations['openshift.infrastructure/worker-count'];
  const redisNamespace = annotations['openshift.infrastructure/redis-namespace'];
  const baseDomain = annotations['openshift.infrastructure/base-domain'];
  const kubeconfigBase64 = annotations['openshift.infrastructure/kubeconfig-base64'];
  const kubeadminPasswordBase64 =
    annotations['openshift.infrastructure/kubeadmin-password-base64'];
  const sshPrivateKeyB64 = annotations['openshift.infrastructure/ssh-private-key'];
  const sshUser = annotations['openshift.infrastructure/ssh-user'] || 'core';
  const gcpProjectId = annotations['gcp.infrastructure/project-id'];
  const gcpRegion = annotations['gcp.infrastructure/region'];

  return (
    <InfoCard
      title="OpenShift on GCP"
      subheader={`Environment: ${environmentName || 'N/A'} | OCP ${version || 'N/A'}`}
      icon={<RiOpenSourceLine size={20} />}
    >
      <InfraStack>
        <InfraSection title="Cluster">
          {clusterName && (
            <InfraField label="Cluster name" value={clusterName} />
          )}
          {apiUrl && (
            <InfraField
              label="API URL"
              value={<ExternalLink href={apiUrl}>{apiUrl}</ExternalLink>}
            />
          )}
          {consoleUrl && (
            <InfraField
              label="Web console"
              value={<ExternalLink href={consoleUrl}>Open console</ExternalLink>}
            />
          )}
          {appsDomain && (
            <InfraField label="Apps domain" value={`*.${appsDomain}`} />
          )}
          {baseDomain && <InfraField label="Base domain" value={baseDomain} />}
        </InfraSection>

        <InfraSection title="Sizing">
          {workerCount && workerType && (
            <InfraField
              label="Workers"
              value={`${workerCount} × ${workerType}`}
            />
          )}
          {gcpRegion && <InfraField label="GCP region" value={gcpRegion} />}
          {gcpProjectId && (
            <InfraField label="GCP project" value={gcpProjectId} />
          )}
        </InfraSection>

        {redisNamespace && (
          <InfraSection title="Redis">
            <InfraField label="Namespace" value={redisNamespace} />
          </InfraSection>
        )}

        <LabOcSection
          clusterName={clusterName}
          environmentName={environmentName}
          kubeconfigBase64={kubeconfigBase64}
          kubeadminPasswordBase64={kubeadminPasswordBase64}
          consoleUrl={consoleUrl}
          redisNamespace={redisNamespace}
        />

        {sshPrivateKeyB64 && (
          <InfraSection title="Node SSH">
            <InfraField
              label="Private key (base64)"
              value={
                <span className={styles.copyRow}>
                  <span className={styles.value}>
                    {secretPreview(sshPrivateKeyB64)}
                  </span>
                  <CopyButton text={sshPrivateKeyB64} label="Copy private key" />
                </span>
              }
              mono={false}
            />
            <InfraField
              label="Usage"
              value={`echo '<base64>' | base64 -d > ocp-${clusterName || 'cluster'}.pem && chmod 600 ocp-${clusterName || 'cluster'}.pem && ssh -i ocp-${clusterName || 'cluster'}.pem ${sshUser}@<node-ip>`}
            />
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
